/**
 * Sketch Schemas
 * @package @genieartinc/schemas
 *
 * Zod validation schemas for the Shader Node sketch system.
 * Covers CRUD operations, library browsing, forking, and AI generation.
 */

import { z } from 'zod';
import { SketchCategorySchema } from '../canvas/sketch/sketch.schema';
export type { SketchCategory } from '../canvas/sketch/sketch.schema';

// ==================== Enums ====================

export const SketchRuntimeModeSchema = z.enum(['p5', 'glsl', 'threejs', 'raw']);
export type SketchRuntimeMode = z.infer<typeof SketchRuntimeModeSchema>;

// ==================== Create ====================

export const SketchCreateSchema = z.object({
  code: z.string().min(1, 'Code is required'),
  runtimeMode: SketchRuntimeModeSchema,
  name: z.string().min(1, 'Name is required').max(255),
  description: z.string().optional(),
  tags: z.array(z.string()).optional().default([]),
  category: SketchCategorySchema,
  canvasId: z.string().optional(),
  nodeId: z.string().optional(),
  isLibraryItem: z.boolean().optional().default(false),
});
export type SketchCreateInput = z.infer<typeof SketchCreateSchema>;

// ==================== Update ====================

export const SketchUpdateSchema = z.object({
  code: z.string().min(1).optional(),
  runtimeMode: SketchRuntimeModeSchema.optional(),
  name: z.string().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  tags: z.array(z.string()).optional(),
  category: SketchCategorySchema.optional(),
  canvasId: z.string().nullable().optional(),
  nodeId: z.string().nullable().optional(),
  isLibraryItem: z.boolean().optional(),
  thumbnailUrl: z.string().url().nullable().optional(),
  previewVideoUrl: z.string().url().nullable().optional(),
});
export type SketchUpdateInput = z.infer<typeof SketchUpdateSchema>;

// ==================== Query ====================

export const SketchListQuerySchema = z.object({
  category: SketchCategorySchema.optional(),
  tags: z.union([z.string(), z.array(z.string())]).optional(),
  search: z.string().optional(),
  page: z.coerce.number().int().min(1).optional().default(1),
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
});
export type SketchListQuery = z.infer<typeof SketchListQuerySchema>;

export const SketchLibraryQuerySchema = z.object({
  category: SketchCategorySchema.optional(),
  tags: z.union([z.string(), z.array(z.string())]).optional(),
  search: z.string().optional(),
  page: z.coerce.number().int().min(1).optional().default(1),
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
});
export type SketchLibraryQuery = z.infer<typeof SketchLibraryQuerySchema>;

// ==================== Fork ====================

export const SketchForkSchema = z.object({
  name: z.string().min(1).max(255).optional(),
});
export type SketchForkInput = z.infer<typeof SketchForkSchema>;

// ==================== AI Generation ====================

export const SketchGenerateSchema = z.object({
  prompt: z.string().min(1, 'Prompt is required').max(4000),
  runtimeMode: z.enum(['p5', 'glsl', 'threejs', 'raw', 'auto']).optional().default('auto'),
  llm: z.string().optional(),
  inputTypes: z.array(z.string()).optional().default([]),
});
export type SketchGenerateInput = z.infer<typeof SketchGenerateSchema>;
